import {
  CustomField,
  CustomFieldEntity,
  CustomFieldPayload,
  CustomFieldType,
  CustomFieldValues,
} from "./customFields.types";

export const FIELD_TYPE_LABELS: Record<CustomFieldType, string> = {
  text: "Text and number",
  number: "Number",
  dropdown: "Dropdown list",
};

export const ENTITY_LABELS: Record<CustomFieldEntity, string> = {
  organization: "organization",
  individual: "individual",
};

export const NO_ACCESS_MESSAGE =
  "You don’t have access to the Settings page. Contact a firm admin if you need to make changes.";

export type CustomFieldDraft = {
  label: string;
  fieldType: CustomFieldType;
  choices: string[];
  entityType: CustomFieldEntity;
};

export const emptyDraft = (
  entityType: CustomFieldEntity,
): CustomFieldDraft => ({
  label: "",
  fieldType: "text",
  choices: [""],
  entityType,
});

export const draftFromField = (field: CustomField): CustomFieldDraft => ({
  label: field.label,
  fieldType: field.fieldType,
  choices:
    field.fieldType === "dropdown" && field.choices.length
      ? [...field.choices]
      : [""],
  entityType: field.entityType,
});

/** The cleaned choices of a draft (blank and repeated ones left out). */
export const cleanChoices = (choices: string[]) => {
  const seen = new Set<string>();
  return choices
    .map((choice) => choice.trim())
    .filter((choice) => {
      const key = choice.toLowerCase();
      if (!choice || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

/** A message when the draft can't be saved yet, otherwise null. */
export const draftError = (draft: CustomFieldDraft): string | null => {
  if (!draft.label.trim()) return "Enter a field name.";
  if (draft.label.trim().length > 100)
    return "The field name can be at most 100 characters.";
  if (
    draft.fieldType === "dropdown" &&
    cleanChoices(draft.choices).length === 0
  )
    return "Add at least one choice.";
  return null;
};

export const draftToPayload = (
  draft: CustomFieldDraft,
  orgId: string,
  id?: string,
): CustomFieldPayload => ({
  id,
  orgId,
  entityType: draft.entityType,
  label: draft.label.trim(),
  fieldType: draft.fieldType,
  choices: draft.fieldType === "dropdown" ? cleanChoices(draft.choices) : [],
});

export const choicesText = (field: CustomField) =>
  field.fieldType === "dropdown" && field.choices.length
    ? field.choices.join(", ")
    : "—";

export const fieldsFor = (
  fields: CustomField[] | undefined,
  entityType: CustomFieldEntity,
) =>
  (fields ?? [])
    .filter((field) => field.entityType === entityType)
    .sort((a, b) => a.sortOrder - b.sortOrder);

/** The fields that have a value, in order, for a client's info card. */
export const filledCustomFields = (
  fields: CustomField[] | undefined,
  entityType: CustomFieldEntity,
  values: CustomFieldValues | undefined,
) =>
  fieldsFor(fields, entityType)
    .map((field) => ({
      id: field.id,
      label: field.label,
      value: (values?.[field.id] ?? "").trim(),
    }))
    .filter((field) => field.value);

/** A message for a value that can't be saved, otherwise null. */
export const valueError = (field: CustomField, value: string | undefined) => {
  const text = (value ?? "").trim();
  if (!text) return null;
  if (
    field.fieldType === "number" &&
    !/^[-+]?(\d{1,3}(,\d{3})+|\d*)(\.\d+)?$/.test(text.replace(/\s/g, ""))
  )
    return `“${field.label}” only accepts numbers.`;
  return null;
};

/** Moves `dragId` to where `targetId` is (drag and drop). */
export const moveField = (ids: string[], dragId: string, targetId: string) => {
  const from = ids.indexOf(dragId);
  const to = ids.indexOf(targetId);
  if (from < 0 || to < 0 || from === to) return ids;
  const next = [...ids];
  next.splice(to, 0, next.splice(from, 1)[0]);
  return next;
};

/** Values for the fields shown, ready to send (empty = remove). */
export const valuesPayload = (
  fields: CustomField[],
  values: CustomFieldValues,
): Record<string, string> =>
  Object.fromEntries(fields.map((field) => [field.id, values[field.id] ?? ""]));

export const valuesChanged = (
  fields: CustomField[],
  saved: CustomFieldValues | undefined,
  values: CustomFieldValues,
) =>
  fields.some(
    (field) =>
      (saved?.[field.id] ?? "").trim() !== (values[field.id] ?? "").trim(),
  );
